import React, { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Typography,
  message
} from 'antd'
import adminService from '../../../services/admin'
import { ADMIN, INSTRUCTOR, STUDENT } from '../../../constants/userRoles'

const { Title } = Typography

const AdminUsers = () => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 25 })
  const [filters, setFilters] = useState({ q: '', role: undefined, isActive: undefined })

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)

  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()

  const load = async (params = {}) => {
    try {
      setLoading(true)
      const res = await adminService.listUsers(params)
      setData(res)
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load({ page: 1, limit: 25 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username'
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role'
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (v) => (v ? 'Yes' : 'No')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setEditingUser(record)
              editForm.setFieldsValue({
                name: record.name,
                email: record.email,
                username: record.username,
                role: record.role,
                isActive: !!record.isActive
              })
              setEditOpen(true)
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete user?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={async () => {
              try {
                await adminService.deleteUser(record._id)
                message.success('User deleted')
                await load({ ...filters, page: data.page, limit: data.limit })
              } catch (e) {
                message.error(e?.response?.data?.message || 'Failed to delete user')
              }
            }}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Users
          </Title>
        </Col>
        <Col>
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            Create User
          </Button>
        </Col>
      </Row>

      <Card style={{ marginBottom: 12 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={10}>
            <Input
              placeholder="Search name/email/username"
              value={filters.q}
              onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              allowClear
              placeholder="Role"
              style={{ width: '100%' }}
              value={filters.role}
              onChange={(v) => setFilters((p) => ({ ...p, role: v }))}
              options={[
                { value: ADMIN, label: 'admin' },
                { value: INSTRUCTOR, label: 'instructor' },
                { value: STUDENT, label: 'student' }
              ]}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              allowClear
              placeholder="Active?"
              style={{ width: '100%' }}
              value={filters.isActive}
              onChange={(v) => setFilters((p) => ({ ...p, isActive: v }))}
              options={[
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' }
              ]}
            />
          </Col>
          <Col xs={24} md={2}>
            <Button
              block
              onClick={() => load({ ...filters, page: 1, limit: data.limit })}
            >
              Go
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        rowKey={(r) => r._id}
        loading={loading}
        columns={columns}
        dataSource={data.items}
        pagination={{
          current: data.page,
          pageSize: data.limit,
          total: data.total,
          showSizeChanger: true,
          onChange: (page, pageSize) => load({ ...filters, page, limit: pageSize })
        }}
      />

      <Modal
        title="Create user"
        visible={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await adminService.createUser(values)
              message.success('User created')
              setCreateOpen(false)
              createForm.resetFields()
              await load({ ...filters, page: 1, limit: data.limit })
            } catch (e) {
              message.error(e?.response?.data?.message || 'Failed to create user')
            }
          }}
        >
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="username" label="Username" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="mobile" label="Mobile (111-111-1111)" rules={[{ required: false }]}>
            <Input placeholder="111-111-1111" />
          </Form.Item>
          <Form.Item name="role" label="Role" initialValue={STUDENT}>
            <Select
              options={[
                { value: ADMIN, label: 'admin' },
                { value: INSTRUCTOR, label: 'instructor' },
                { value: STUDENT, label: 'student' }
              ]}
            />
          </Form.Item>
          <Form.Item name="password" label="Password" rules={[{ required: true, min: 7 }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit user"
        visible={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => editForm.submit()}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await adminService.updateUser(editingUser._id, values)
              message.success('User updated')
              setEditOpen(false)
              setEditingUser(null)
              await load({ ...filters, page: data.page, limit: data.limit })
            } catch (e) {
              message.error(e?.response?.data?.message || 'Failed to update user')
            }
          }}
        >
          <Form.Item name="name" label="Name">
            <Input />
          </Form.Item>
          <Form.Item name="username" label="Username">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Role">
            <Select
              options={[
                { value: ADMIN, label: 'admin' },
                { value: INSTRUCTOR, label: 'instructor' },
                { value: STUDENT, label: 'student' }
              ]}
            />
          </Form.Item>
          <Form.Item label="Active">
            <Form.Item name="isActive" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default AdminUsers

